import { Link, useParams } from 'react-router-dom';
import { getDrop } from '../data/mock';
import { useCurrency } from '../lib/currency';
import { geneUrl } from '../lib/memegenes';
import { GeneCheckIcon } from '../components/GeneCheckIcon';

/**
 * Drop Detail — full-bleed content hero, drop terms, Collect CTA, and a
 * prominent provenance block linking out to the Gene on MemeGenes. This is
 * where "MemeGenes is present, not invisible" actually shows up. See BRAND.md
 * ▸ Page Templates ▸ Drop Detail.
 */
export function DropDetail() {
  const { id } = useParams();
  const { format } = useCurrency();
  const drop = id ? getDrop(id) : undefined;

  if (!drop) {
    return (
      <main className="mx-auto max-w-7xl px-6 py-24 text-center">
        <h1 className="font-heading text-3xl font-bold tracking-tight">Drop not found</h1>
        <p className="mt-3 text-provny-muted">This original may have moved or never existed.</p>
        <Link to="/explore" className="mt-6 inline-block font-semibold text-provny-cyber-dim hover:underline dark:text-provny-cyber">
          ← Back to Explore
        </Link>
      </main>
    );
  }

  const soldOut = drop.edition.sold >= drop.edition.total;
  const pct = drop.edition.total > 0 ? Math.round((drop.edition.sold / drop.edition.total) * 100) : 0;

  return (
    <main className="mx-auto max-w-7xl px-6 py-8">
      <Link to="/explore" className="text-sm text-provny-muted transition-colors hover:text-provny-black dark:hover:text-provny-white">
        ← Explore
      </Link>

      <div className="mt-6 grid grid-cols-1 gap-10 lg:grid-cols-2">
        {/* Content hero */}
        <div className="overflow-hidden rounded-2xl border border-provny-border bg-provny-surface dark:border-provny-border-dark dark:bg-provny-surface-dark">
          <img
            src={drop.image}
            alt={drop.title}
            className={`w-full object-cover ${soldOut ? 'opacity-70' : ''}`}
          />
        </div>

        {/* Drop terms */}
        <div className="flex flex-col gap-6">
          <div>
            <Link
              to={`/c/${drop.channelSlug}`}
              className="text-xs font-medium uppercase tracking-wider text-provny-muted transition-colors hover:text-provny-black dark:hover:text-provny-white"
            >
              {drop.channel}
            </Link>
            <h1 className="mt-2 font-heading text-4xl font-black tracking-tight">{drop.title}</h1>
            <Link
              to={`/@${drop.creator}`}
              className="mt-2 inline-block text-provny-muted transition-colors hover:text-provny-black dark:hover:text-provny-white"
            >
              @{drop.creator}
            </Link>
          </div>

          {drop.story && <p className="text-lg leading-relaxed text-provny-black/80 dark:text-provny-white/80">{drop.story}</p>}

          {/* Edition + price */}
          <div className="rounded-2xl border border-provny-border bg-provny-surface p-5 dark:border-provny-border-dark dark:bg-provny-surface-dark">
            <div className="flex items-baseline justify-between font-mono">
              <span className={soldOut ? 'font-medium text-provny-gold' : 'text-provny-muted'}>
                {soldOut ? 'SOLD OUT' : `${drop.edition.sold} / ${drop.edition.total} collected`}
              </span>
              <span className="text-lg font-semibold">{format(drop.priceSol)}</span>
            </div>

            <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-provny-border dark:bg-provny-border-dark">
              <div
                className={`h-full rounded-full ${soldOut ? 'bg-provny-gold' : 'bg-provny-cyber'}`}
                style={{ width: `${pct}%` }}
              />
            </div>

            {drop.endsInLabel && !soldOut && (
              <p className="mt-3 font-mono text-sm text-provny-muted">{drop.endsInLabel}</p>
            )}

            {soldOut ? (
              <button
                disabled
                className="mt-4 w-full cursor-not-allowed rounded-xl border border-provny-border py-3 font-semibold text-provny-muted dark:border-provny-border-dark"
              >
                Sold Out
              </button>
            ) : (
              <button className="mt-4 w-full rounded-xl bg-provny-cyber py-3 font-semibold text-provny-ink transition hover:bg-provny-cyber/90">
                Collect Now · {format(drop.priceSol)}
              </button>
            )}
          </div>

          {/* Provenance — MemeGenes present, not invisible */}
          <a
            href={geneUrl(drop.geneId)}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-4 rounded-2xl border border-provny-border p-5 transition-colors hover:border-provny-gold dark:border-provny-border-dark"
          >
            <GeneCheckIcon size={28} />
            <div className="flex-1">
              <p className="font-semibold">Verified original</p>
              <p className="text-sm text-provny-muted">
                Invisible proof of origin, embedded at publish. View this original's Gene on MemeGenes.
              </p>
            </div>
            <span className="font-mono text-sm text-provny-muted transition-colors group-hover:text-provny-gold">
              {drop.geneId} →
            </span>
          </a>
        </div>
      </div>
    </main>
  );
}
